import React from 'react';
import { Button } from './button';
import { Minus, Plus } from 'lucide-react';

/**
 * StepperInput - Mobile-friendly numeric stepper control
 * 
 * @param {number} value - Current value
 * @param {function} onChange - Callback when value changes
 * @param {number} min - Minimum value (default: 0)
 * @param {number} max - Maximum value (default: 999)
 * @param {number} step - Increment/decrement step (default: 1)
 * @param {string} label - Optional label text
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Whether the control is disabled
 */
export const StepperInput = ({
  value,
  onChange,
  min = 0,
  max = 999,
  step = 1,
  label,
  className = '',
  disabled = false,
  'data-testid': testId
}) => {
  const numValue = typeof value === 'number' ? value : parseInt(value) || 0;
  
  const handleDecrement = () => {
    const newValue = Math.max(min, numValue - step);
    onChange(newValue);
  };
  
  const handleIncrement = () => {
    const newValue = Math.min(max, numValue + step);
    onChange(newValue);
  };
  
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    // Allow empty input while typing
    if (inputValue === '') {
      onChange('');
      return;
    }
    const parsed = parseInt(inputValue);
    if (!isNaN(parsed)) {
      onChange(Math.min(max, Math.max(min, parsed)));
    }
  };
  
  const handleBlur = () => {
    // Normalize value on blur
    if (value === '' || value === null || value === undefined) {
      onChange(min);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handleDecrement}
        disabled={disabled || numValue <= min}
        className="h-10 w-10 shrink-0 rounded-lg border-slate-200"
        data-testid={testId ? `${testId}-decrement` : undefined}
      >
        <Minus className="h-4 w-4" />
      </Button>
      
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        disabled={disabled}
        className="h-10 w-16 text-center border border-slate-200 rounded-lg bg-white text-base font-medium focus:outline-none focus:ring-2 focus:ring-[#004B8D]/20 focus:border-[#004B8D]"
        data-testid={testId}
      />
      
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={handleIncrement}
        disabled={disabled || numValue >= max}
        className="h-10 w-10 shrink-0 rounded-lg border-slate-200"
        data-testid={testId ? `${testId}-increment` : undefined}
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default StepperInput;
